import React, { FC, PropsWithChildren, useContext } from 'react';

import styled from 'styled-components';

// theme
import aampeTheme from '../../../../constants/aampeTheme';

import { EditorContext } from 'context/EditorContext';

// models
import { MobilePushPreviewModel } from '../../../../models/editor.model';

// components
import Image from '../shared/Image';
import MobilePushButtonPreview from './buttons/MobilePushButtonPreview';

export interface TextContentProps {
    hasImage?: boolean;
    phonePreview?: boolean;
    isSkeleton?: boolean;
}
//moved this here instead of in the helper file because it makes storybook throw an  undefined error with enums
const truncateText = (heading: string, truncationCount: number) =>
    heading?.length > truncationCount
        ? heading.slice(0, truncationCount) + '...'
        : heading;

const MobilePushMessagePreview: FC<PropsWithChildren<MobilePushPreviewModel>> =
    ({
        id,
        image_url,
        content,
        buttons,
        isSkeleton,
        phonePreview,
        isSecondaryView,
    }) => {
        const {
            editorDetails: { showSmallPhone },
        } = useContext(EditorContext);
        return (
            <OuterContainer phonePreview={phonePreview}>
                <Message
                    phonePreview={phonePreview}
                    showSmallPhone={showSmallPhone}
                    key={id}
                >
                    <MessageContent>
                        <TextContent hasImage={!!image_url}>
                            <Header isSkeleton={isSkeleton}>
                                {truncateText(content.header, 40)}
                            </Header>
                            <Body isSkeleton={isSkeleton}>{content.body}</Body>
                        </TextContent>
                        {image_url && (
                            <Image
                                initialUrl={image_url}
                                height={'50px'}
                                width={'50px'}
                                isCropped={true}
                            />
                        )}
                    </MessageContent>
                    {isSecondaryView && (
                        <ActionButtons>
                            {buttons?.map((button) => (
                                <Button key={button.id}>{button.text}</Button>
                            ))}
                        </ActionButtons>
                    )}
                </Message>
                {!isSecondaryView && (
                    <IosButtons>
                        {buttons && buttons.length > 0 && (
                            <MobilePushButtonPreview buttons={buttons} />
                        )}
                    </IosButtons>
                )}
            </OuterContainer>
        );
    };

export default MobilePushMessagePreview;

const OuterContainer = styled.div.withConfig({
    shouldForwardProp: (prop) =>
        prop !== 'isSkeleton' && prop !== 'phonePreview',
})<{ isSkeleton?: boolean; phonePreview?: boolean }>(
    ({ isSkeleton, phonePreview }) => ({
        display: 'flex',
        flexDirection: 'column',
        gap: aampeTheme.spacing4(1),
        justifyContent: 'space-between',
        alignItems: 'center',
        width: phonePreview ? '82%' : '242px',
        minHeight: isSkeleton ? '140px' : 'auto', // Fixed invalid `none` value
    }),
);

const Message = styled.div.withConfig({
    shouldForwardProp: (prop) => prop !== 'phonePreview',
})<{ phonePreview?: boolean; showSmallPhone?: boolean }>(
    ({ phonePreview, showSmallPhone }) => ({
        background: phonePreview
            ? aampeTheme.colors.white
            : 'linear-gradient(279deg, #F2F7FC 1.91%, #F9FBFE 16.23%, #FFF 39.11%)',
        boxShadow: '0px 4px 6px 2px rgba(13, 99, 184, 0.08)',
        borderRadius: aampeTheme.borderRadius.message,
        border: `1px solid ${aampeTheme.colors.white}`,
        padding: showSmallPhone
            ? aampeTheme.spacing4(3)
            : aampeTheme.spacing4(4),
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
        maxWidth: '100%',
        height: 'fit-content',
        whiteSpace: 'pre-wrap',
        gap: aampeTheme.spacing4(3), // Add some space between message and buttons
    }),
);
const MessageContent = styled.div({
    display: 'flex',
    flexFlow: 'row nowrap',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
});

const TextContent = styled.div.withConfig({
    shouldForwardProp: (prop) => prop !== 'hasImage',
})<{ hasImage?: boolean }>(({ hasImage }) => ({
    display: 'flex',
    flexFlow: 'column nowrap',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    width: hasImage ? '190px' : '260px',
    height: 'fit-content',
    fontSize: aampeTheme.size.font.textSmall,
    lineHeight: aampeTheme.size.lineHeight.small,
}));

const Header = styled.div.withConfig({
    shouldForwardProp: (prop) => prop !== 'isSkeleton',
})<{ isSkeleton?: boolean }>(({ isSkeleton }) => ({
    color: isSkeleton ? 'transparent' : aampeTheme.colors.textAccessibleWG02,
    fontWeight: aampeTheme.weights.heavy,
    wordBreak: 'break-word',
    display: 'inline-block',
    textShadow: isSkeleton
        ? `0 0 4px ${aampeTheme.colors.textAccessibleWG02}`
        : 'none',
}));

const Body = styled.div.withConfig({
    shouldForwardProp: (prop) => prop !== 'isSkeleton',
})<{ isSkeleton?: boolean }>(({ isSkeleton }) => ({
    color: isSkeleton ? 'transparent' : aampeTheme.colors.neutral09,
    fontSize: aampeTheme.size.font.textSmall,
    wordBreak: 'break-word',
    display: 'inline-block',
    fontWeight: isSkeleton ? 600 : 400,
    textShadow: isSkeleton ? `0 0 4px ${aampeTheme.colors.solidWG02}` : 'none',
}));
const ActionButtons = styled.div({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    width: '100%',
    maxWidth: '100%',
});
const IosButtons = styled.div({
    display: 'flex',
    flexDirection: 'column',
    gap: '1px',
    width: '100%',
});

const Button = styled.button({
    background: aampeTheme.colors.white,
    minWidth: '46px',
    maxWidth: '100%',
    border: 'none',
    display: 'block', // Change from `flex` to `block` to avoid flex content issues
    textAlign: 'left',
    borderRadius: aampeTheme.borderRadius.default,
    height: aampeTheme.spacing4(5),
    fontSize: aampeTheme.size.font.textSmall,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    boxSizing: 'border-box',

    lineHeight: aampeTheme.size.lineHeight.small,
    fontWeight: aampeTheme.weights.light,
});
